import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { FileSpreadsheet, FileArchive, Smartphone, Building2, ArrowRight } from "lucide-react";
import { KpiNumber } from "@/components/common/KpiNumber";
import { exportRows } from "@/components/portal/CandidatesTable";
import { fullName, fmt } from "@/lib/demo/utils";
import { useI18n } from "@/lib/i18n/i18n";

const Card = ({ children, className = "", testId }) => <div className={`rounded-[24px] border border-slate-200 bg-white p-6 ${className}`} data-testid={testId}>{children}</div>;
const Eyebrow = ({ children }) => { const { t } = useI18n(); return <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">{t(children)}</p>; };
const tip = { contentStyle: { borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 } };

export const PortalResumen = ({ stats, rows, onOpen }) => {
  const { t } = useI18n();
  return (
  <div className="space-y-6" data-testid="portal-resumen">
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {[["Candidatos", stats.total, "total", true], ["Hoy", stats.today, "today"], ["Día 1", stats.day1, "day1"], ["Día 2", stats.day2, "day2"]].map(([l, v, id, hl]) => (
        <Card key={id} className={hl ? "border-slate-950 bg-slate-950 text-white" : ""}>
          <p className={`text-[11px] font-bold uppercase tracking-[0.2em] ${hl ? "text-white/50" : "text-slate-400"}`}>{t(l)}</p>
          <KpiNumber value={v} testId={`portal-kpi-${id}`} className="mt-3 font-display text-5xl font-bold tracking-tight" />
        </Card>
      ))}
    </div>
    <div className="grid gap-6 xl:grid-cols-3">
      <Card className="xl:col-span-2" testId="portal-hourly-chart">
        <Eyebrow>Candidatos recibidos por hora</Eyebrow>
        <div className="mt-4 h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={stats.hourly} barGap={2}>
              <CartesianGrid vertical={false} stroke="#F1F5F9" />
              <XAxis dataKey="hour" tickLine={false} axisLine={false} fontSize={11} stroke="#94A3B8" />
              <YAxis tickLine={false} axisLine={false} fontSize={11} stroke="#94A3B8" width={30} />
              <Tooltip {...tip} cursor={{ fill: "#F8FAFC" }} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="dia1" name={t("Día 1 · 12 mayo")} fill="#CBD5E1" radius={[6, 6, 0, 0]} />
              <Bar dataKey="dia2" name={t("Día 2 · hoy")} fill="#8B0000" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
      <Card testId="portal-latest">
        <Eyebrow>Últimos candidatos</Eyebrow>
        <ul className="mt-4 space-y-1">
          {rows.slice(0, 6).map((r) => (
            <li key={r.interaction.id}>
              <button onClick={() => onOpen(r)} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-slate-50" data-testid={`portal-latest-${r.participant.id}`}>
                <span className="font-mono text-xs font-bold text-slate-500">{r.interaction.time}</span>
                <span className="truncate text-sm font-semibold">{fullName(r.participant)}</span>
                {r.interaction.id.startsWith("live-") && <span className="ml-auto h-2 w-2 shrink-0 rounded-full bg-upv" />}
              </button>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  </div>
  );
};

const HBar = ({ data, color, testId }) => {
  const { t } = useI18n();
  return (
  <div className="mt-4 h-72" data-testid={testId}>
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data.map((d) => ({ ...d, name: t(d.name) }))} layout="vertical" margin={{ left: 10 }}>
        <XAxis type="number" hide />
        <YAxis type="category" dataKey="name" width={150} tickLine={false} axisLine={false} fontSize={12} stroke="#475569" />
        <Tooltip {...tip} cursor={{ fill: "#F8FAFC" }} formatter={(v) => fmt(v)} />
        <Bar dataKey="value" name={t("Candidatos")} fill={color} radius={[0, 6, 6, 0]} barSize={18} />
      </BarChart>
    </ResponsiveContainer>
  </div>
  );
};

export const PortalStats = ({ stats }) => (
  <div className="grid gap-6 xl:grid-cols-2" data-testid="portal-stats">
    <Card><Eyebrow>Candidatos por titulación</Eyebrow><HBar data={stats.titulaciones} color="#8B0000" testId="portal-stats-titulacion" /></Card>
    <Card><Eyebrow>Candidatos por centro</Eyebrow><HBar data={stats.centros} color="#0F172A" testId="portal-stats-centro" /></Card>
    <Card className="xl:col-span-2">
      <Eyebrow>Lecturas hoy por dispositivo</Eyebrow>
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        {stats.devices.map((d) => (
          <div key={d.id} className="rounded-2xl bg-slate-50 p-5" data-testid={`portal-stats-device-${d.id}`}>
            <Smartphone className="h-4 w-4 text-upv" /><p className="mt-3 text-sm font-semibold">{d.label}</p>
            <KpiNumber value={d.value} className="mt-1 font-display text-3xl font-bold" />
          </div>
        ))}
      </div>
    </Card>
  </div>
);

export const PortalExport = ({ rows, total }) => {
  const { t } = useI18n();
  return (
  <div className="grid gap-6 lg:grid-cols-2" data-testid="portal-export">
    <Card>
      <FileSpreadsheet className="h-8 w-8 text-emerald-700" />
      <h3 className="mt-5 font-display text-2xl font-bold">{t("Candidatos en Excel")}</h3>
      <p className="mt-2 text-sm text-slate-500">{t("Nombre, contacto, titulación, centro, fecha, hora y dispositivo de lectura. {n} candidatos recibidos.", { n: fmt(total) })}</p>
      <button onClick={() => exportRows(rows, t)} className="mt-6 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-emerald-700 font-bold tracking-wide text-white transition-colors hover:bg-emerald-800" data-testid="portal-export-excel-button"><FileSpreadsheet className="h-4 w-4" /> {t("EXPORTAR EXCEL")}</button>
    </Card>
    <Card>
      <FileArchive className="h-8 w-8 text-upv" />
      <h3 className="mt-5 font-display text-2xl font-bold">{t("Todos los CV (ZIP)")}</h3>
      <p className="mt-2 text-sm text-slate-500">{t("Descarga masiva de los currículums recibidos, nombrados por candidato.")}</p>
      <button onClick={() => toast(t("Descarga ZIP"), { description: t("Disponible en la versión final.") })} className="mt-6 flex h-12 w-full items-center justify-center gap-2 rounded-xl border-2 border-slate-900 font-bold tracking-wide transition-colors hover:bg-slate-900 hover:text-white" data-testid="portal-export-zip-button"><FileArchive className="h-4 w-4" /> {t("DESCARGAR CV")}</button>
    </Card>
  </div>
  );
};

export const PortalCuenta = ({ stats }) => {
  const { t } = useI18n();
  return (
  <div className="grid gap-6 lg:grid-cols-2" data-testid="portal-cuenta">
    <Card>
      <Building2 className="h-8 w-8 text-upv" />
      <h3 className="mt-5 font-display text-2xl font-bold">Bosch</h3>
      <dl className="mt-4 space-y-3 text-sm">
        {[["Evento", "Foro de Empleo UPV 2027"], ["Stand", "A-12"], ["Sector", "Tecnología e ingeniería"], ["Usuario", "Recruiting Team · Bosch"]].map(([k, v]) => (
          <div key={k} className="flex justify-between border-b border-slate-100 pb-3"><dt className="text-slate-500">{t(k)}</dt><dd className="font-semibold">{t(v)}</dd></div>
        ))}
      </dl>
    </Card>
    <Card>
      <Eyebrow>Dispositivos de reclutadores</Eyebrow>
      <ul className="mt-4 space-y-2">
        {stats.devices.map((d) => (
          <li key={d.id} className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3" data-testid={`portal-cuenta-device-${d.id}`}>
            <span className="flex items-center gap-3 text-sm font-semibold"><span className="h-2 w-2 rounded-full bg-emerald-500" />{d.label}</span>
            <span className="text-xs text-slate-500">{t("Activo · {n} lecturas hoy", { n: fmt(d.value) })}</span>
          </li>
        ))}
      </ul>
      <a href="/empresa/scanner" className="mt-4 flex items-center justify-between rounded-xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white" data-testid="portal-open-scanner-link">{t("Abrir scanner")} <ArrowRight className="h-4 w-4" /></a>
    </Card>
  </div>
  );
};
